import React, { Component } from 'react';
import ReactEcharts from 'echarts-for-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import LocaleFormat from '../../commons/utils/LocaleFormat.js';
import ConfigBusiness from '../../commons/config/ConfigBusiness.js';
import { descargarChartPng } from '../../commons/utils/chartPng.js';

// Mapa locale → código de moneda (mismo que EricAnalyst).
var LOCALE_TO_CURRENCY = {
  'es-AR': 'ARS', 'pt-BR': 'BRL', 'en-US': 'USD',
  'es-CL': 'CLP', 'es-CO': 'COP', 'es-MX': 'MXN',
  'es-UY': 'UYU', 'es-PY': 'PYG',
};

function getEricCurrency() {
  var locale = ConfigBusiness.get('generales.formatoRegional') || 'es-AR';
  return LOCALE_TO_CURRENCY[locale] || 'ARS';
}

/**
 * Formatea un valor de eje/serie según el campo `formato` de la chart_spec.
 * formato ∈ 'numero' | 'moneda' | 'porcentaje'
 */
function formatearValor(v, formato) {
  if (v === null || v === undefined) return '';
  if (formato === 'moneda') {
    return LocaleFormat.formatCurrency(v, getEricCurrency());
  }
  if (formato === 'porcentaje') {
    return LocaleFormat.formatNumber(v, 2) + '%';
  }
  // 'numero' u omitido
  var n = Number(v);
  if (!isNaN(n)) {
    return LocaleFormat.formatNumber(n, Number.isInteger(n) ? 0 : 2);
  }
  return String(v);
}

/**
 * Traduce una chart_spec genérica + filas de datos → opción de ECharts 4.x.
 *
 * Soporta: bar, line, area, pie, doughnut, scatter.
 * El mapa de calor (mapa_calor) se maneja aparte con Leaflet.
 *
 * @param {object} chartSpec  - chart_spec del micro
 * @param {Array}  filas      - filas del dataset
 * @returns {object}  opción para ECharts, o null si no se puede traducir
 */
function buildEchartsOption(chartSpec, filas) {
  if (!chartSpec || !chartSpec.tipo || chartSpec.tipo === 'mapa_calor') return null;
  if (!chartSpec.eje_x || !chartSpec.serie) return null;
  if (!filas || filas.length === 0) return null;

  var tipo     = chartSpec.tipo;
  var campoX   = chartSpec.eje_x.campo;
  var campoY   = chartSpec.serie.campo;
  var formato  = chartSpec.serie.formato || 'numero';
  var labelX   = chartSpec.eje_x.label || campoX;
  var labelY   = chartSpec.serie.label || campoY;
  var titulo   = chartSpec.titulo || '';
  var labels   = chartSpec.labels || [];

  var xData = filas.map(function(f) { return f[campoX] !== undefined ? String(f[campoX]) : ''; });
  var yData = filas.map(function(f) { return f[campoY] !== undefined ? Number(f[campoY]) : 0; });

  // Si el micro incluyó labels formateados, preferirlos sobre los valores raw del campo X.
  var xLabels = (labels.length === xData.length) ? labels : xData;

  // ---- PIE / DOUGHNUT ----
  if (tipo === 'pie' || tipo === 'doughnut') {
    var pieData = xLabels.map(function(label, i) {
      return { name: label, value: yData[i] };
    });
    return {
      title: titulo ? {
        text: titulo,
        left: 'center',
        textStyle: { fontSize: 14, fontWeight: 'normal', color: '#333' },
      } : undefined,
      tooltip: {
        trigger: 'item',
        formatter: function(params) {
          return params.name + '<br/>' + labelY + ': '
            + formatearValor(params.value, formato)
            + ' (' + params.percent + '%)';
        },
      },
      legend: {
        orient: 'horizontal',
        bottom: 0,
        type: 'scroll',
      },
      series: [{
        type: 'pie',
        radius: tipo === 'doughnut' ? ['40%', '70%'] : '60%',
        center: ['50%', '45%'],
        data: pieData,
        label: {
          formatter: function(params) {
            return params.name + '\n' + formatearValor(params.value, formato);
          },
        },
      }],
    };
  }

  // ---- SCATTER ----
  if (tipo === 'scatter') {
    var scatterData = filas.map(function(f) {
      return [f[campoX] !== undefined ? Number(f[campoX]) : 0,
              f[campoY] !== undefined ? Number(f[campoY]) : 0];
    });
    return {
      title: titulo ? {
        text: titulo,
        left: 'center',
        textStyle: { fontSize: 14, fontWeight: 'normal', color: '#333' },
      } : undefined,
      tooltip: {
        trigger: 'item',
        formatter: function(params) {
          return labelX + ': ' + formatearValor(params.value[0], 'numero')
            + '<br/>' + labelY + ': ' + formatearValor(params.value[1], formato);
        },
      },
      xAxis: { name: labelX, type: 'value' },
      yAxis: {
        name: labelY,
        type: 'value',
        axisLabel: {
          formatter: function(v) { return formatearValor(v, formato); },
        },
      },
      series: [{
        type: 'scatter',
        data: scatterData,
        symbolSize: 7,
      }],
    };
  }

  // ---- BAR, LINE, AREA ----
  var echartsType = (tipo === 'line' || tipo === 'area') ? 'line' : 'bar';
  var serieExtra = tipo === 'area' ? { areaStyle: {} } : {};

  return {
    title: titulo ? {
      text: titulo,
      left: 'center',
      textStyle: { fontSize: 14, fontWeight: 'normal', color: '#333' },
    } : undefined,
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        var p = params[0];
        return p.name + '<br/>' + labelY + ': ' + formatearValor(p.value, formato);
      },
    },
    xAxis: {
      type: 'category',
      data: xLabels,
      name: labelX,
      axisLabel: { rotate: xLabels.length > 10 ? 30 : 0, interval: 0 },
    },
    yAxis: {
      type: 'value',
      name: labelY,
      axisLabel: {
        formatter: function(v) { return formatearValor(v, formato); },
      },
    },
    grid: { containLabel: true, left: '3%', right: '4%', bottom: xLabels.length > 10 ? '15%' : '3%' },
    series: [Object.assign({}, serieExtra, {
      type: echartsType,
      data: yData,
      smooth: tipo === 'line' || tipo === 'area',
    })],
  };
}

/**
 * EricChart — renderiza un gráfico ECharts a partir de una chart_spec genérica.
 *
 * Props:
 *   chartSpec  {object}  - chart_spec del microservicio
 *   filas      {Array}   - dataset (filas del response)
 *   columnas   {Array}   - columnas del dataset
 *   fechaDato  {string}  - ISO string del created_at del mensaje (para aviso de antigüedad)
 *   intl       {object}  - objeto intl de react-intl (para i18n)
 *   mapaId     {string}  - id único para el container del mapa Leaflet (mapa_calor)
 *
 * Uso típico desde EricAnalyst:
 *   <EricChart chartSpec={mensaje.chartSpec} filas={mensaje.filas}
 *              columnas={mensaje.columnas} fechaDato={mensaje.fechaEnvio}
 *              intl={this.props.intl} mapaId={'eric-mapa-' + index} />
 */
export class EricChart extends Component {
  constructor(props) {
    super(props);
    this._mapaRef = React.createRef();
    this._mapaLeaflet = null;
    this._echartsComp = null;
  }

  componentDidMount() {
    if (this.props.chartSpec && this.props.chartSpec.tipo === 'mapa_calor') {
      this._inicializarMapa();
    }
  }

  componentDidUpdate(prevProps) {
    var spec = this.props.chartSpec;
    var prevSpec = prevProps.chartSpec;
    if (!spec || spec.tipo !== 'mapa_calor') return;
    // Si el spec o los datos cambiaron, re-inicializar el mapa.
    if (spec !== prevSpec || this.props.filas !== prevProps.filas) {
      if (this._mapaLeaflet) {
        this._mapaLeaflet.remove();
        this._mapaLeaflet = null;
      }
      this._inicializarMapa();
    }
  }

  componentWillUnmount() {
    if (this._mapaLeaflet) {
      this._mapaLeaflet.remove();
      this._mapaLeaflet = null;
    }
  }

  _inicializarMapa() {
    var spec = this.props.chartSpec;
    if (!spec || spec.tipo !== 'mapa_calor') return;
    var el = this._mapaRef.current;
    if (!el) return;
    if (el._leaflet_id) return; // ya inicializado

    var colLat = spec.eje_x && spec.eje_x.campo;
    var colLon = spec.serie && spec.serie.campo;
    if (!colLat || !colLon) return;

    var filas = this.props.filas || [];
    var puntos = [];
    filas.forEach(function(f) {
      var lat = parseFloat(f[colLat]);
      var lon = parseFloat(f[colLon]);
      if (!isNaN(lat) && !isNaN(lon)) {
        puntos.push([lat, lon]);
      }
    });
    if (puntos.length === 0) return;

    var sumLat = 0;
    var sumLon = 0;
    puntos.forEach(function(p) { sumLat += p[0]; sumLon += p[1]; });
    var centerLat = sumLat / puntos.length;
    var centerLon = sumLon / puntos.length;

    var map = L.map(el);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 18,
    }).addTo(map);
    L.heatLayer(puntos, { radius: 25, blur: 15, minOpacity: 0.4 }).addTo(map);
    map.setView([centerLat, centerLon], 8);
    this._mapaLeaflet = map;
  }

  _nombreArchivo() {
    var spec = this.props.chartSpec;
    if (spec && spec.titulo) {
      return spec.titulo.replace(/[^a-zA-Z0-9_\-\s]/g, '').trim().replace(/\s+/g, '_') || 'datos_eric';
    }
    return 'datos_eric';
  }

  // Texto "datos al {fecha}" compartido por el aviso visible y el caption del PNG.
  // Devuelve null si no hay fecha del dato.
  _captionFecha() {
    var fechaDato = this.props.fechaDato;
    if (!fechaDato) return null;
    var formateada = LocaleFormat.formatDate(fechaDato);
    if (!formateada) return null;
    var intl = this.props.intl;
    return intl
      ? intl.formatMessage({ id: 'eric.chart.datos_al', defaultMessage: 'datos al {fecha}' }, { fecha: formateada })
      : ('datos al ' + formateada);
  }

  renderAviso() {
    var texto = this._captionFecha();
    if (!texto) return null;
    return (
      <div className="eric-chart-aviso-fecha">
        <i className="fa fa-clock-o mr-1" aria-hidden="true"></i>
        {texto}
      </div>
    );
  }

  // Método público: lo invoca EricAnalyst desde la fila de acciones del mensaje
  // (junto a like/dislike/copiar) vía ref. No-op si el gráfico es un mapa de calor
  // (Leaflet, sin instancia de ECharts).
  descargarPng() {
    var inst = (this._echartsComp && typeof this._echartsComp.getEchartsInstance === 'function')
      ? this._echartsComp.getEchartsInstance()
      : null;
    if (!inst) return;
    var spec = this.props.chartSpec || {};
    descargarChartPng(inst, {
      titulo: spec.titulo || '',
      caption: this._captionFecha() || '',
      nombreArchivo: this._nombreArchivo(),
    });
  }

  render() {
    var spec    = this.props.chartSpec;
    var filas   = this.props.filas || [];

    if (!spec || !spec.tipo) return null;

    // ---- MAPA DE CALOR ----
    if (spec.tipo === 'mapa_calor') {
      return (
        <div className="eric-chart-wrapper">
          {spec.titulo && <div className="eric-chart-titulo">{spec.titulo}</div>}
          <div className="eric-mapa-calor-wrapper">
            <div ref={this._mapaRef} className="eric-mapa-calor-container" />
          </div>
          {this.renderAviso()}
        </div>
      );
    }

    // ---- GRÁFICOS ECHARTS ----
    var option = buildEchartsOption(spec, filas);
    if (!option) return null;

    var self = this;
    return (
      <div className="eric-chart-wrapper">
        <ReactEcharts
          ref={function(e) { self._echartsComp = e; }}
          option={option}
          style={{ height: '340px', width: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
        {this.renderAviso()}
      </div>
    );
  }
}

export default EricChart;
